/**
 * The Data tab's plain logic (components/data, services/DataService.js): how a store is named and summed up, the rail's
 * groups by service, the table edits staged before they are applied, a document's diff, which query writes.
 */

export const STORE_KINDS = {
  postgres: { label: 'PostgreSQL', language: 'sql', noun: ['table', 'tables'] },
  mysql: { label: 'MySQL', language: 'sql', noun: ['table', 'tables'] },
  sqlite: { label: 'SQLite', language: 'sql', noun: ['table', 'tables'] },
  mongodb: { label: 'MongoDB', language: 'mongo', noun: ['collection', 'collections'] },
  redis: { label: 'Redis', language: 'redis', noun: ['key', 'keys'] },
  neo4j: { label: 'Neo4j', language: 'cypher', noun: ['label', 'labels'] },
  s3: { label: 'S3', language: null, noun: ['bucket', 'buckets'] },
  kafka: { label: 'Kafka', language: 'kafka', noun: ['topic', 'topics'] },
};

export const LANGUAGES = [
  { id: 'sql', label: 'SQL', kinds: ['postgres', 'mysql', 'sqlite'] },
  { id: 'mongo', label: 'Mongo', kinds: ['mongodb'] },
  { id: 'cypher', label: 'Cypher', kinds: ['neo4j'] },
  { id: 'redis', label: 'Redis', kinds: ['redis'] },
  { id: 'kafka', label: 'Kafka', kinds: ['kafka'] },
];

/**
 * Every datastore the sandbox supports, in the rail's order, with what each is for (one line) and how a prompt names
 * it. The Data tab lists the ones a project does not use yet as "Available" (a static list: GET /data is unchanged).
 */
export const SUPPORTED_KINDS = [
  { kind: 'postgres', purpose: 'Tables and SQL', noun: 'a PostgreSQL database' },
  { kind: 'mysql', purpose: 'Tables and SQL, MySQL-compatible', noun: 'a MySQL database' },
  { kind: 'mongodb', purpose: 'Documents and JSON', noun: 'a MongoDB database' },
  { kind: 'redis', purpose: 'Cache and queues', noun: 'a Redis cache' },
  { kind: 'neo4j', purpose: 'Graphs and relationships', noun: 'a Neo4j graph database' },
  { kind: 'sqlite', purpose: 'A small SQL database in a file', noun: 'a SQLite database' },
  { kind: 's3', purpose: 'Files and images', noun: 'an S3 bucket for files' },
  { kind: 'kafka', purpose: 'Event streams', noun: 'a Kafka topic' },
];

/** The supported kinds no store of the overview has, in SUPPORTED_KINDS order. */
export function unusedKinds(stores = []) {
  const used = new Set(stores.map((s) => s.kind));
  return SUPPORTED_KINDS.filter((k) => !used.has(k.kind));
}

/** "Add a MongoDB database to this project's backend service: " (the person goes on with what it is for). */
export function addStorePrompt(kind, service = null) {
  const noun = SUPPORTED_KINDS.find((k) => k.kind === kind)?.noun || `a ${kindLabel(kind)} datastore`;
  const name = serviceLabel(service);
  return `Add ${noun} to this project${name ? `'s ${name} service` : ''}: `;
}

export const kindLabel = (kind) => STORE_KINDS[kind]?.label || kind || 'Store';
export const languageOf = (kind) => STORE_KINDS[kind]?.language || null;
export const isQueryable = (store) => Boolean(store && languageOf(store.kind));

/** "PostgreSQL · catalog" (the name only when it says more than the kind). */
export function storeLabel(store) {
  if (!store) { return ''; }
  const label = kindLabel(store.kind);
  return store.name && store.name.toLowerCase() !== label.toLowerCase() && store.name !== store.kind ? `${label} · ${store.name}` : label;
}

export function formatCount(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) { return ''; }
  return Number(n).toLocaleString('en-US');
}

export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) { return ''; }
  if (n < 1024) { return `${n} B`; }
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = n / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit += 1; }
  return `${value >= 10 ? Math.round(value) : Math.round(value * 10) / 10} ${units[unit]}`;
}

const plural = (n, [one, many]) => `${formatCount(n)} ${n === 1 ? one : many}`;

/** The rail's second line: "4 tables · 38 MB", "212 keys", "Stopped", "No data yet". */
export function storeSummary(store) {
  if (!store) { return ''; }
  if (store.status === 'stopped') { return 'Stopped'; }
  const objects = store.objects || [];
  const noun = STORE_KINDS[store.kind]?.noun || ['object', 'objects'];
  let first;
  if (store.kind === 'redis') {
    const keys = objects.reduce((sum, o) => sum + (Number(o.count) || 0), 0);
    first = plural(keys, noun);
  } else if (store.kind === 's3') {
    const files = objects.reduce((sum, o) => sum + (Number(o.count) || 0), 0);
    first = objects.length === 1 ? plural(files, ['file', 'files']) : plural(objects.length, noun);
  } else {
    first = plural(objects.length, noun);
  }
  if (store.status === 'empty' && !objects.length) { return 'No data yet'; }
  const size = store.sizeBytes ? formatBytes(store.sizeBytes) : '';
  return [first, size].filter(Boolean).join(' · ');
}

/** "services/backend" → "backend". */
export const serviceLabel = (service) => String(service || '').replace(/^services\//, '');

/** The rail's groups: each store under the first service that uses it, in the order the services first appear. */
export function groupStores(stores = []) {
  const groups = [];
  const byName = new Map();
  stores.forEach((store) => {
    const name = store.services?.length ? serviceLabel(store.services[0]) : '';
    if (!byName.has(name)) {
      const group = { name, label: name || 'Not used by a service', stores: [] };
      byName.set(name, group);
      groups.push(group);
    }
    byName.get(name).stores.push(store);
  });
  // The stores no service declares go last.
  return groups.sort((a, b) => (a.name ? 0 : 1) - (b.name ? 0 : 1));
}

/** The object a store opens on: the first with data (or the first). */
export function firstObject(store) {
  const objects = store?.objects || [];
  return (objects.find((o) => Number(o.count) > 0) || objects[0])?.name || null;
}

/** The store the tab opens on: the first running one with something in it, else the first. */
export function firstStore(stores = []) {
  return stores.find((s) => s.status === 'running' && s.objects?.length) || stores.find((s) => s.status === 'running') || stores[0] || null;
}

// ---- Values ----

const VECTOR_TYPE = /^(vector|halfvec|sparsevec)\b/i;
const VECTOR_SHOWN = 4;

/**
 * A pgvector value as a short text: `vector · 1536 dims [0.12, -0.03, 0.5, 0.91, …]` (a sparsevec: its non-zero
 * entries out of its dimensions). Null when `value` is not one.
 */
export function vectorText(value, column = {}) {
  if (typeof value !== 'string' || !VECTOR_TYPE.test(String(column.type || ''))) { return null; }
  const kind = String(column.type).match(VECTOR_TYPE)[1].toLowerCase();
  const sparse = /^\{(.*)\}\/(\d+)$/.exec(value.trim());
  if (sparse) {
    const entries = sparse[1] ? sparse[1].split(',') : [];
    return `${kind} · ${entries.length} of ${sparse[2]} dims {${entries.slice(0, VECTOR_SHOWN).join(', ')}${entries.length > VECTOR_SHOWN ? ', …' : ''}}`;
  }
  const dense = /^\[(.*)\]$/.exec(value.trim());
  if (!dense) { return null; }
  const items = dense[1] ? dense[1].split(',') : [];
  const shown = items.slice(0, VECTOR_SHOWN).map((n) => { const x = Number(n); return Number.isFinite(x) ? String(Math.round(x * 1000) / 1000) : n.trim(); });
  return `${kind} · ${items.length} dims [${shown.join(', ')}${items.length > VECTOR_SHOWN ? ', …' : ''}]`;
}

/** A cell's text: NULL, a binary's size, a vector's dimensions and first values, JSON for objects. */
export function displayValue(value, column = null) {
  if (value === null || value === undefined) { return 'NULL'; }
  const vector = column ? vectorText(value, column) : null;
  if (vector) { return vector; }
  if (typeof value === 'object') {
    if (value.$binary !== undefined) { return `binary · ${formatBytes(value.size ?? 0)}`; }
    return JSON.stringify(value);
  }
  return String(value);
}

/** The text an edited cell starts from. */
export const editText = (value) => (value === null || value === undefined ? 'NULL' : typeof value === 'object' ? JSON.stringify(value) : String(value));

const NUMERIC_INT = /^(int|integer|smallint|bigint|int2|int4|int8|serial|bigserial|smallserial)$/i;
const NUMERIC_FLOAT = /^(real|float|float4|float8|double precision|double)$/i;
const BOOLEAN = /^(bool|boolean)$/i;
const JSON_TYPE = /^jsonb?$/i;

/**
 * What a typed cell sends: `NULL` (as typed) is null; numbers for integer and float columns (bigint and numeric stay
 * text, as the server sends them), true/false for booleans, the parsed value for json columns; else the text.
 */
export function parseCellInput(text, column = {}) {
  const value = String(text ?? '');
  if (value === 'NULL') { return null; }
  // MySQL's display widths and flags (`int(11)`, `bigint(20) unsigned`) do not change what is sent
  const type = String(column.type || '').trim().replace(/\(\d+\)(\s+(unsigned|zerofill))*$/i, '');
  if (/^(bigint|int8|bigserial)$/i.test(type)) { return value.trim(); }
  if (NUMERIC_INT.test(type) || NUMERIC_FLOAT.test(type)) {
    const n = Number(value.trim());
    return value.trim() !== '' && Number.isFinite(n) ? n : value;
  }
  if (BOOLEAN.test(type)) {
    if (/^(true|t|1|yes)$/i.test(value.trim())) { return true; }
    if (/^(false|f|0|no)$/i.test(value.trim())) { return false; }
    return value;
  }
  if (JSON_TYPE.test(type)) {
    try { return JSON.parse(value); } catch { return value; }
  }
  return value;
}

export const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// ---- Staged table edits ----

/** The primary key values of a row, `{ id: 1182 }`; null without a key. */
export function keyOf(row, primaryKey = []) {
  if (!row || !primaryKey?.length) { return null; }
  return Object.fromEntries(primaryKey.map((column) => [column, row[column]]));
}

/** A row's identity among the staged edits. */
export const rowIdOf = (row, primaryKey) => {
  const key = keyOf(row, primaryKey);
  return key ? JSON.stringify(key) : null;
};

export const emptyPending = () => ({ edits: {}, inserts: [], deletes: {} });

/** Stages a cell edit; the same value as before takes the cell (and an untouched row) out again. */
export function stageEdit(pending, row, primaryKey, column, value) {
  const id = rowIdOf(row, primaryKey);
  if (!id) { return pending; }
  const current = pending.edits[id] || { key: keyOf(row, primaryKey), values: {}, original: {} };
  const values = { ...current.values };
  const original = { ...current.original };
  if (sameValue(value, row[column])) {
    delete values[column];
    delete original[column];
  } else {
    values[column] = value;
    original[column] = row[column];
  }
  const edits = { ...pending.edits };
  if (Object.keys(values).length) { edits[id] = { ...current, values, original }; } else { delete edits[id]; }
  return { ...pending, edits };
}

let insertSeq = 0;
export function stageInsert(pending, values = {}) {
  insertSeq += 1;
  return { ...pending, inserts: [...pending.inserts, { id: `new-${Date.now()}-${insertSeq}`, values }] };
}

/** Stages many new rows at once (a CSV import): `rows: [{ values, line }]`, each marked with the file it came from. */
export function stageInserts(pending, rows, { file = null } = {}) {
  insertSeq += 1;
  const at = `${Date.now()}-${insertSeq}`;
  const added = rows.map((row, i) => ({ id: `imp-${at}-${i}`, values: row.values, ...(file ? { imported: file } : {}), ...(row.line ? { line: row.line } : {}) }));
  return { ...pending, inserts: [...pending.inserts, ...added] };
}

/** Takes out the staged rows with these ids (a CSV import's rows that would fail, or all of a file's). */
export const removeInserts = (pending, ids) => {
  const gone = new Set(ids);
  return { ...pending, inserts: pending.inserts.filter((ins) => !gone.has(ins.id)) };
};

export function updateInsert(pending, insertId, column, value) {
  return {
    ...pending,
    inserts: pending.inserts.map((ins) => {
      if (ins.id !== insertId) { return ins; }
      const values = { ...ins.values };
      if (value === '' || value === undefined) { delete values[column]; } else { values[column] = value; }
      return { ...ins, values };
    }),
  };
}

export const removeInsert = (pending, insertId) => ({ ...pending, inserts: pending.inserts.filter((ins) => ins.id !== insertId) });

/** Marks rows for deletion (or, when all are marked already, takes the mark off). Their edits go. */
export function toggleDeletes(pending, rows, primaryKey) {
  const ids = rows.map((row) => rowIdOf(row, primaryKey)).filter(Boolean);
  const allMarked = ids.length > 0 && ids.every((id) => pending.deletes[id]);
  const deletes = { ...pending.deletes };
  const edits = { ...pending.edits };
  rows.forEach((row) => {
    const id = rowIdOf(row, primaryKey);
    if (!id) { return; }
    if (allMarked) { delete deletes[id]; } else {
      deletes[id] = keyOf(row, primaryKey);
      delete edits[id];
    }
  });
  return { ...pending, deletes, edits };
}

export function pendingCounts(pending) {
  const edits = Object.keys(pending.edits).length;
  const inserts = pending.inserts.length;
  const deletes = Object.keys(pending.deletes).length;
  return { edits, inserts, deletes, total: edits + inserts + deletes };
}

/** "1 edit · 1 new row · 1 delete". */
export function pendingText(pending) {
  const { edits, inserts, deletes } = pendingCounts(pending);
  return [
    edits ? `${edits} edit${edits === 1 ? '' : 's'}` : '',
    inserts ? `${inserts} new row${inserts === 1 ? '' : 's'}` : '',
    deletes ? `${deletes} delete${deletes === 1 ? '' : 's'}` : '',
  ].filter(Boolean).join(' · ');
}

/** What each of buildChanges' changes came from: `{ insertId, line? }` for a staged row, null for an edit or a delete. */
export function changeSources(pending) {
  return [
    ...Object.values(pending.edits).map(() => null),
    ...pending.inserts.filter((ins) => Object.keys(ins.values).length).map((ins) => ({ insertId: ins.id, ...(ins.line ? { line: ins.line } : {}) })),
    ...Object.values(pending.deletes).map(() => null),
  ];
}

/** The contract's `changes` for the staged edits of one table: updates, then inserts, then deletes. */
export function buildChanges(pending, { table, schema } = {}) {
  const where = schema ? { table, schema } : { table };
  return [
    ...Object.values(pending.edits).map((edit) => ({ op: 'update', ...where, key: edit.key, values: edit.values })),
    ...pending.inserts.filter((ins) => Object.keys(ins.values).length).map((ins) => ({ op: 'insert', ...where, values: ins.values })),
    ...Object.values(pending.deletes).map((key) => ({ op: 'delete', ...where, key })),
  ];
}

// ---- JSON ----

/**
 * Parses JSON, also as people type it in a Mongo shell: unquoted keys (`{ rating: { $lte: 2 } }`) and single-quoted
 * strings. `{ ok, value }` or `{ ok: false, error }`; empty text is `{ ok: true, value: undefined }`.
 */
export function parseLooseJson(text) {
  const source = String(text ?? '').trim();
  if (!source) { return { ok: true, value: undefined }; }
  try {
    return { ok: true, value: JSON.parse(source) };
  } catch (error) {
    try {
      const fixed = source
        .replace(/'((?:[^'\\]|\\.)*)'/g, (_, inner) => JSON.stringify(inner.replace(/\\'/g, "'")))
        .replace(/([{,]\s*)([$A-Za-z_][\w$.]*)\s*:/g, '$1"$2":');
      return { ok: true, value: JSON.parse(fixed) };
    } catch {
      return { ok: false, error: error.message };
    }
  }
}

const short = (value) => {
  const text = JSON.stringify(value);
  return text && text.length > 40 ? `${text.slice(0, 37)}…` : text;
};

/** What changes from one document to the other, by top-level field: `status "published" → "hidden"`, `added photos`. */
export function docDiff(before = {}, after = {}) {
  const lines = [];
  const a = before && typeof before === 'object' ? before : {};
  const b = after && typeof after === 'object' ? after : {};
  Object.keys(b).forEach((key) => {
    if (!(key in a)) { lines.push(`added ${key}`); } else if (!sameValue(a[key], b[key])) { lines.push(`${key} ${short(a[key])} → ${short(b[key])}`); }
  });
  Object.keys(a).forEach((key) => { if (!(key in b)) { lines.push(`removed ${key}`); } });
  return lines;
}

/** A document's id for its title: `ObjectId("65f1c2…a9")`, or the value. */
export function docIdLabel(id) {
  if (id && typeof id === 'object' && id.$oid) {
    const oid = String(id.$oid);
    return `ObjectId("${oid.length > 10 ? `${oid.slice(0, 6)}…${oid.slice(-2)}` : oid}")`;
  }
  return typeof id === 'string' ? `"${id}"` : JSON.stringify(id);
}

// ---- Queries ----

const SQL_WRITE = /\b(insert|update|delete|merge|create|drop|alter|truncate|grant|revoke|vacuum|reindex|comment\s+on|copy|call|do)\b/i;
const CYPHER_WRITE = /\b(create|merge|set|delete|detach|remove|drop|load\s+csv)\b/i;
const REDIS_READ = new Set(['GET', 'MGET', 'HGETALL', 'HGET', 'LRANGE', 'SMEMBERS', 'ZRANGE', 'TTL', 'TYPE', 'SCAN', 'EXISTS', 'XRANGE', 'STRLEN', 'HLEN', 'LLEN', 'SCARD', 'ZCARD']);
const MONGO_WRITE_KEYS = ['insert', 'insertOne', 'insertMany', 'update', 'updateOne', 'updateMany', 'replaceOne', 'delete', 'deleteOne', 'deleteMany', 'drop', 'findOneAndUpdate', 'findOneAndDelete', 'findOneAndReplace', 'bulkWrite', 'createIndex', 'dropIndex'];

const stripSqlComments = (text) => text.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ').replace(/'(?:[^']|'')*'/g, "''");

/**
 * Whether a query would change data (it then asks first and runs with allowWrite): SQL and Cypher by their keywords
 * (outside strings and comments), Redis anything not on the server's read list, Mongo a write command or $out/$merge.
 */
export function queryWrites(language, text) {
  const source = String(text || '');
  if (!source.trim()) { return false; }
  if (language === 'sql') { return SQL_WRITE.test(stripSqlComments(source)); }
  if (language === 'cypher') { return CYPHER_WRITE.test(source.replace(/\/\/[^\n]*/g, ' ').replace(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'/g, '""')); }
  if (language === 'redis') {
    const command = source.trim().split(/\s+/)[0].toUpperCase();
    return !REDIS_READ.has(command);
  }
  if (language === 'mongo') {
    const parsed = parseLooseJson(source);
    if (!parsed.ok || !parsed.value || typeof parsed.value !== 'object') { return false; }
    if (MONGO_WRITE_KEYS.some((key) => key in parsed.value)) { return true; }
    return /"\$(out|merge)"/.test(JSON.stringify(parsed.value.aggregate || []));
  }
  return false;
}

/**
 * A Kafka query (the server's JSON language, vfs-service src/data/kafkaQuery.js) for a topic: by the name the Data tab
 * shows, or `raw` with the real name when a prefixed and an unprefixed topic share it (`topics`: the topic list).
 */
export function kafkaQuery(topic, topics = [], { where = {}, limit = 50 } = {}) {
  const row = typeof topic === 'string' ? null : topic;
  const name = row ? row.name : topic;
  const twin = row && topics.filter((t) => t.name === row.name).length > 1;
  return JSON.stringify({ topic: twin ? row.topic : name, ...(twin ? { raw: true } : {}), where, limit }, null, 2);
}

/** What the Kafka query takes, in one line under the editor. */
export const KAFKA_QUERY_HELP = 'where: { "value.priority": "high", "key": "a1" } · "$contains", "$gt" / "$lt", "$in", "$ne", "$exists" · "since": "-1h" or a date · "partition", "fromOffset" · newest first unless since / fromOffset · reads at most 5,000 messages';

/** The "Ask in words" placeholder of each language. */
export const ASK_PLACEHOLDERS = {
  sql: 'Describe what you want to see, e.g. the 10 most recent orders with their totals',
  mongo: 'Describe what you want to see, e.g. the documents created today, newest first',
  cypher: 'Describe what you want to see, e.g. the nodes connected to a given one',
  redis: 'Describe what you want to see, e.g. the keys that start with session',
  kafka: 'Describe what you want to see, e.g. the last 10 messages with priority high',
};

/** A first query for a store (and an object of it), so the box is never empty. */
export function sampleQuery(store, object) {
  const name = object || firstObject(store) || '';
  switch (store?.kind) {
    case 'postgres':
    case 'sqlite': {
      const obj = (store.objects || []).find((o) => o.name === name);
      const table = obj?.schema && obj.schema !== 'public' ? `${obj.schema}.${name}` : name;
      return name ? `SELECT * FROM ${table} LIMIT 50;` : 'SELECT 1;';
    }
    case 'mysql':
      return name ? `SELECT * FROM \`${name.replace(/`/g, '``')}\` LIMIT 50;` : 'SELECT 1;';
    case 'mongodb':
      return JSON.stringify({ collection: name || 'collection', find: {}, limit: 50 }, null, 2);
    case 'neo4j':
      return name ? `MATCH (n:\`${name}\` {project: $project})\nRETURN n LIMIT 50` : 'MATCH (n {project: $project})\nRETURN n LIMIT 50';
    case 'redis':
      return 'SCAN 0 MATCH * COUNT 100';
    case 'kafka':
      return kafkaQuery(name || 'messages');
    default:
      return '';
  }
}

/** CSV of a result (header row, then the rows; objects as JSON). */
export function toCsv(columns = [], rows = []) {
  const cell = (value) => {
    const text = value === null || value === undefined ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  return [columns.map(cell).join(','), ...rows.map((row) => columns.map((column) => cell(Array.isArray(row) ? row[columns.indexOf(column)] : row?.[column])).join(','))].join('\n');
}

/** The column names of a result (`columns` as names or `{ name }`, else the rows' keys). */
export function columnNames(result) {
  if (result?.columns?.length) { return result.columns.map((c) => (typeof c === 'string' ? c : c.name)); }
  const names = [];
  (result?.rows || []).forEach((row) => {
    if (row && typeof row === 'object' && !Array.isArray(row)) { Object.keys(row).forEach((key) => { if (!names.includes(key)) { names.push(key); } }); }
  });
  return names;
}

/** "2 h ago", "yesterday", "3 days ago", else the date. */
export function ago(value, now = Date.now()) {
  const at = typeof value === 'number' ? value : Date.parse(value);
  if (!Number.isFinite(at)) { return ''; }
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) { return 'just now'; }
  if (s < 3600) { return `${Math.floor(s / 60)} min ago`; }
  if (s < 86400) { return `${Math.floor(s / 3600)} h ago`; }
  if (s < 172800) { return 'yesterday'; }
  if (s < 7 * 86400) { return `${Math.floor(s / 86400)} days ago`; }
  return new Date(at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "TTL 9 min", "no TTL". `ttl`: seconds; -1 or null: none. */
export function ttlText(ttl) {
  const n = Number(ttl);
  if (ttl === null || ttl === undefined || !Number.isFinite(n) || n < 0) { return 'no TTL'; }
  if (n < 60) { return `TTL ${n} s`; }
  if (n < 3600) { return `TTL ${Math.round(n / 60)} min`; }
  if (n < 86400) { return `TTL ${Math.round(n / 3600)} h`; }
  return `TTL ${Math.round(n / 86400)} d`;
}

/** What a file is, from its type or name: 'image' | 'pdf' | 'text' | 'other'. */
export function fileKind(key = '', contentType = '') {
  const type = String(contentType || '').toLowerCase();
  const name = String(key || '').toLowerCase();
  if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|avif|bmp)$/.test(name)) { return 'image'; }
  if (type === 'application/pdf' || name.endsWith('.pdf')) { return 'pdf'; }
  if (type.startsWith('text/') || /json|xml|yaml|csv|javascript/.test(type) || /\.(txt|json|md|csv|xml|ya?ml|log|html|css|js)$/.test(name)) { return 'text'; }
  return 'other';
}

/** The last part of an object key (`shoes/2026-10/a.jpg` → `a.jpg`; a folder keeps its slash). */
export function baseName(key = '') {
  const text = String(key);
  const trimmed = text.endsWith('/') ? text.slice(0, -1) : text;
  const name = trimmed.slice(trimmed.lastIndexOf('/') + 1);
  return text.endsWith('/') ? `${name}/` : name;
}

// ---- Stores in Docker ----
// A compose file's mongo, an image service's postgres (vfs-service src/data/compose): in their own rail group, apart
// from the stores Flow runs. The panel keeps them as stores with `docker: true` and `status` running or stopped;
// `dockerStatus` is what vfs-service said (running, stopped, docker-off, not-run).

/** The rail store of a store in Docker. */
export function dockerPanelStore(store) {
  return { ...store, docker: true, dockerStatus: store.status, status: store.status === 'running' ? (store.empty && !(store.objects || []).length ? 'empty' : 'running') : 'stopped' };
}

/** Where it is declared, in words: "In compose: mongo (services/stack)", "Image: postgres:16 (services/db)". */
export function dockerWhere(store) {
  if (!store) { return ''; }
  const folder = store.service || '';
  if (store.source === 'image') { return `Image: ${store.image} (${folder})`; }
  if (store.source === 'dockerfile') { return `Dockerfile: ${store.image} (${folder})`; }
  return `In compose: ${store.composeService || store.name} (${folder})`;
}

/** The rail's line under a store in Docker. */
export function dockerSummary(store) {
  if (!store) { return ''; }
  if (store.dockerStatus === 'docker-off') { return 'Docker is off'; }
  if (store.dockerStatus === 'not-run') { return 'Not run by Flow'; }
  if (store.dockerStatus !== 'running') { return 'Stopped'; }
  return `${kindLabel(store.kind)} · ${storeSummary(store)}`;
}
